import React from 'react';
import Markdown from 'marked-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { Seo } from '../components/site/Seo';
import { TeamCredential } from '../components/site/TeamCredential';
import { useContentfulCollection } from '../hooks/useContentfulCollection';
import { OptimizedImage } from '../components/site/OptimizedImage';
import { Link } from 'react-router-dom';

const richTextOptions = {
  renderMark: {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
    [MARKS.STRIKETHROUGH]: (text) => <s>{text}</s>,
    [MARKS.SUPERSCRIPT]: (text) => <sup>{text}</sup>,
    [MARKS.SUBSCRIPT]: (text) => <sub>{text}</sub>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
    [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
    [BLOCKS.UL_LIST]: (node, children) => <ul className='bullet-list'>{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol className='bullet-list'>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => <blockquote className='detail-quote'>{children}</blockquote>,
    [BLOCKS.HR]: () => <hr />,
    [INLINES.HYPERLINK]: (node, children) => (
      <a href={node.data.uri} target='_blank' rel='noopener noreferrer'>{children}</a>
    ),
  },
};

function TeamMemberCard({ member }) {
  return (
    <article className='profile-card'>
      <div className='profile-image-shell'>
        <OptimizedImage
          src={member.photo || '/team/profile-default.png'}
          alt={member.name}
          className='profile-image'
          fit='contain'
          sizes='(min-width: 1100px) 25vw, 100vw'
        />
      </div>
      <div className='profile-copy'>
        {member.role ? <p className='card-eyebrow'>{member.role}</p> : null}
        <h3>{member.name}</h3>
        {member.bioRichText ? (
          <div className='markdown-body rich-text-body profile-bio-rich-text'>
            {documentToReactComponents(member.bioRichText, richTextOptions)}
          </div>
        ) : member.bio ? (
          <div className='markdown-body profile-bio-rich-text'>
            <Markdown>{member.bio}</Markdown>
          </div>
        ) : null}
        {member.credentials?.length ? (
          <div className='profile-credentials'>
            {member.credentials.map((credential, index) => (
              <TeamCredential
                key={`${member.slug || member.name}-${credential.name || index}`}
                credential={credential}
              />
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}

export function LeadershipPage() {
  const { items: team } = useContentfulCollection('teamMembers', { pageSize: 50 });
  const executive = team.filter((m) => m.group === 'executive' || !m.group);
  const technical = team.filter((m) => m.group === 'technical');

  return (
    <>
      <Seo
        title='Leadership | Rivelin Robotics'
        description='Meet the Rivelin Robotics leadership team.'
      />
      <div className='page-shell'>
        <section className='page-intro'>
          <div className='page-intro-copy' style={{ textAlign: 'center' }}>
            <h1>Team</h1>
          </div>
        </section>
        {executive.length ? (
          <section className='content-section'>
            <h2 className='team-group-heading' style={{ textAlign: 'center' }}>Executive team</h2>
            <div className='collection-grid team-preview-grid'>
              {executive.map((member) => (
                <TeamMemberCard key={member.slug || member.name} member={member} />
              ))}
            </div>
          </section>
        ) : null}
        {technical.length ? (
          <section className='content-section'>
            <h2 className='team-group-heading' style={{ textAlign: 'center' }}>Technical leads</h2>
            <div className='collection-grid team-preview-grid'>
              {technical.map((member) => (
                <TeamMemberCard key={member.slug || member.name} member={member} />
              ))}
            </div>
          </section>
        ) : null}
        <section className='content-section'>
          <div className='hero-actions' style={{ justifyContent: 'center' }}>
            <Link className='primary-cta' to='/careers'>View open roles</Link>
          </div>
        </section>
      </div>
    </>
  );
}
